import React from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Paper,
  Button, Chip, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Business, Wifi, Devices, Security, Lock, Router,
  Backup, CheckCircle, Shield, Visibility, Groups,
  PointOfSale, DevicesOther, Policy, Verified
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateAndScroll } from '../utils/navigation';
import photos from '../data/photos';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  backgroundImage: `linear-gradient(rgba(30,58,138,0.82), rgba(124,58,237,0.82)), url("${photos.heroes.businessAssessment}")`,
  backgroundSize: '100% auto',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: '#1e3a8a',
  color: 'white',
  padding: '40px 0 50px',
  textAlign: 'center',
});

function BusinessAssessmentPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const businessChecks = [
    {
      photo: photos.businessAssessment.networkInfra,
      title: 'Network Infrastructure',
      items: [
        'Business router and firewall configuration audit',
        'WiFi encryption strength and access controls',
        'Guest network isolation from business systems',
        'Network segmentation between departments or functions',
        'Remote access and VPN configuration review',
      ]
    },
    {
      photo: photos.businessAssessment.endpoints,
      title: 'Endpoint & Device Security',
      items: [
        'Workstation antivirus and endpoint protection status',
        'Operating system and application patch levels',
        'Mobile device management (MDM) policy review',
        'Printer, scanner, and copier security settings',
        'USB and removable media policy evaluation',
      ]
    },
    {
      photo: photos.businessAssessment.businessSystems,
      title: 'Business-Specific Systems',
      items: [
        'Point-of-sale (POS) system security review',
        'Business software and SaaS application audit',
        'Shared drive and file server access permissions',
        'Email system security settings (SPF, DKIM, DMARC)',
        'Customer data storage and handling practices',
      ]
    },
    {
      photo: photos.businessAssessment.employeeAccess,
      title: 'Employee & Access Controls',
      items: [
        'Employee account and password policy evaluation',
        'Admin and privileged access audit: who has the keys?',
        'BYOD (bring your own device) policy assessment',
        'Former employee access termination verification',
        'Multi-factor authentication adoption review',
      ]
    },
    {
      photo: photos.businessAssessment.dataProtection,
      title: 'Data Protection & Recovery',
      items: [
        'Business data backup strategy and frequency',
        'Disaster recovery readiness assessment',
        'Cloud storage configuration and sharing review',
        'Sensitive data handling (customer PII, financial records)',
        'Ransomware recovery capability evaluation',
      ]
    },
    {
      photo: photos.businessAssessment.compliance,
      title: 'Policy & Compliance',
      items: [
        'Existing security policies documentation review',
        'Employee security awareness training status',
        'Incident response plan evaluation (if any)',
        'Industry-specific compliance considerations',
        'Physical security for servers and networking equipment',
      ]
    },
  ];

  return (
    <Box>
      <SEO title="Business Assessment" description="Small business cybersecurity assessment for $599.99. 2-hour on-site for 10 or fewer employees." />
      {/* Hero */}
      <HeroSection>
        <Container maxWidth="lg">
          <Chip icon={<Business sx={{ color: 'white !important' }} />} label="For Small Businesses"
            sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Small Business Cybersecurity Assessment
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 780, mx: 'auto', mb: 4 }}>
            Small businesses are the #1 target for cybercriminals. 43% of all cyberattacks target businesses with 10 or fewer employees. Our comprehensive $599.99 assessment identifies your vulnerabilities before attackers do.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigateAndScroll(navigate, '/book', location.pathname)}
            sx={{ backgroundColor: '#fbbf24', color: '#1e1e1e', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', border: '2px solid #fbbf24', px: 5, py: 1.5, fontSize: '1.2rem', fontWeight: 700 }}>
            Book Business Assessment: $599.99
          </Button>
        </Container>
      </HeroSection>

      {/* Why Businesses Need This */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          Why Your Business Needs This
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[
            { stat: '43%', label: 'of cyberattacks target small businesses (10 or fewer employees)' },
            { stat: '60%', label: 'of small businesses close within 6 months of a major breach' },
            { stat: '$200K', label: 'average cost of a data breach for a small business' },
            { stat: '$599.99', label: 'cost of a CWC assessment to reduce your risk' },
          ].map((item, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card sx={{ textAlign: 'center', py: 3, height: '100%' }}>
                <Typography variant="h3" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {item.stat}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>{item.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* What We Assess */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            What We Assess For Your Business
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}>
            Our business assessment covers everything in a home assessment plus business-specific concerns that put your company and customers at risk
          </Typography>

          <Grid container spacing={3}>
            {businessChecks.map((cat, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ height: '100%', borderTop: '4px solid', borderImage: 'linear-gradient(135deg, #2563eb, #7c3aed) 1' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box component="img" src={cat.photo} alt={cat.title}
                        sx={{ width: 48, height: 48, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>{cat.title}</Typography>
                    </Box>
                    {cat.items.map((item, j) => (
                      <Box key={j} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main', mt: 0.4, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">{item}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Common Findings */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          What We Commonly Find in Business Assessments
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
          These are real, recurring issues, and any one of them could be the entry point for an attack:
        </Typography>
        {[
          'Former employee accounts still active with full access to business systems and email',
          'Business WiFi using a simple password shared with every employee and posted on the wall',
          'No separate guest network, so customers and vendors are on the same network as POS systems',
          'POS systems running outdated software with known vulnerabilities',
          'Business data stored on personal Dropbox or Google Drive accounts with no access controls',
          'No MFA (multi-factor authentication) on email, banking, or business-critical applications',
          'Zero backup strategy, or backups that haven\'t been tested in months',
          'Shared admin passwords written on sticky notes at the front desk',
          'Employee personal devices connecting to the business network with no MDM policy',
          'No incident response plan, so no one knows what to do if a breach happens',
        ].map((finding, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
            <Visibility sx={{ color: '#dc2626', mt: 0.3, flexShrink: 0, fontSize: 20 }} />
            <Typography variant="body1">{finding}</Typography>
          </Box>
        ))}
      </Container>

      {/* What You Receive */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            What Your Business Receives
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {[
              { title: '2-Hour On-Site Assessment', desc: 'A vetted intern spends two full hours systematically evaluating your business network, devices, access controls, and data handling practices, providing twice the depth of a home assessment.' },
              { title: '15-Minute Consultation', desc: 'Immediate walkthrough of critical findings so you can address urgent issues right away.' },
              { title: 'Expert-Reviewed Business Report', desc: 'A CISSP-certified professional reviews all findings within 48 hours, adding business context and compliance considerations.' },
              { title: 'Prioritized Remediation Roadmap', desc: 'A ranked list of actions telling you what to fix today, this week, and this month, with estimated effort and business impact for each item.' },
              { title: 'CWC Business Security Certificate', desc: 'Documented proof of assessment. Useful for client trust, insurance purposes, and compliance requirements.' },
              { title: 'Employee Security Recommendations', desc: 'A set of actionable recommendations you can share with your team to improve security awareness company-wide.' },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Verified sx={{ color: '#7c3aed', mt: 0.5, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Multi-location note */}
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <Paper elevation={0} sx={{ p: 4, backgroundColor: 'rgba(124,58,237,0.05)', borderRadius: 3, border: '1px solid rgba(124,58,237,0.2)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Multiple Locations?</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            If your business has multiple locations, we can coordinate assessments across all sites. Each location is assessed individually at the same $599.99 flat rate. For businesses with more than 10 employees, multiple locations, or enterprise needs, contact us to discuss custom engagement options and pricing.
          </Typography>
          <Button variant="outlined" onClick={() => navigateAndScroll(navigate, '/contact', location.pathname)} sx={{ fontWeight: 600 }}>
            Contact Us for Multi-Location Pricing
          </Button>
        </Paper>
      </Container>

      {/* CTA */}
      <Box sx={{ backgroundImage: `linear-gradient(rgba(30,58,138,0.85), rgba(124,58,237,0.85)), url("${photos.heroes.businessAssessment}")`,
        backgroundSize: 'cover', backgroundPosition: 'center', py: 8 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" color="white" gutterBottom sx={{ fontWeight: 700 }}>
            Don't Wait for a Breach
          </Typography>
          <Typography variant="h6" color="white" sx={{ mb: 4, opacity: 0.9 }}>
            $599.99 now is a lot less than $200,000 later. Know where your business stands today.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigateAndScroll(navigate, '/book', location.pathname)}
            sx={{ backgroundColor: '#fbbf24', color: '#1e1e1e', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', border: '2px solid #fbbf24', px: 5, py: 1.5, fontSize: '1.2rem', fontWeight: 700 }}>
            Book My Business Assessment
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

export default BusinessAssessmentPage;
